import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownOutlined,
  RightOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type {
  AssignableUser,
  KeycloakDirectoryUser,
  KeycloakGroup,
  SpaceMember,
  SpaceRole,
} from '@lens/shared';
import { SPACE_ROLES } from '@lens/shared';
import { ApiError } from '../../../shared/api/client.ts';
import {
  listAssignableUsers,
  listKeycloakGroupChildren,
  searchKeycloakGroups,
  searchKeycloakUsers,
} from '../api.ts';
import { ROLE_LABEL } from './labels.ts';

/**
 * Выдача доступа к пространству: люди и группы Keycloak в одном окне.
 *
 * Людей ищем и среди заведённых в Lens, и в Keycloak — второе нужно, чтобы
 * дать доступ тому, кто ещё ни разу не входил (его запись заведётся при
 * добавлении). Группа же привязывается насовсем: состав пересчитывается сам.
 */

/** Сколько ждать после последней буквы, прежде чем идти в Keycloak. */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Высота списка: тянем его на всё свободное окно, а не режем страницами по
 * восемь строк. Вычитаем шапку модалки, вкладку, поиск и подвал.
 */
const LIST_HEIGHT = 'calc(100vh - 340px)';

export interface GrantAccessModalProps {
  open: boolean;
  spaceId: string;
  members: SpaceMember[];
  onClose: () => void;
  /** Добавить выбранных людей. Возвращает текст итога для сообщения. */
  onAddUsers: (input: {
    userIds: string[];
    keycloakIds: string[];
    role: SpaceRole;
  }) => Promise<void>;
  /** Привязать выбранные группы. */
  onLinkGroups: (input: { keycloakIds: string[]; role: SpaceRole }) => Promise<void>;
  /** Настроен ли вход через Keycloak — без него вкладка групп бесполезна. */
  keycloakConfigured: boolean;
}

/** Строка дерева групп: та же группа плюс её уровень вложенности. */
interface GroupRow extends KeycloakGroup {
  level: number;
}

/** Строка таблицы людей: из Lens, из Keycloak или из обоих сразу. */
interface PersonRow {
  key: string;
  userId: string | null;
  keycloakId: string | null;
  login: string;
  displayName: string;
  email: string | null;
  /**
   * Откуда у человека доступ к пространству: `manual` — выдан руками,
   * `group` — пришёл из группы Keycloak, `null` — доступа нет.
   * Пришедшего из группы выбрать можно: добавление закрепит за ним
   * личную роль, и пересчёт состава группы её больше не тронет.
   */
  memberSource: 'manual' | 'group' | null;
  /** Записи в Lens ещё нет — заведём при добавлении. */
  isNew: boolean;
  enabled: boolean;
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function GrantAccessModal({
  open,
  spaceId,
  members,
  onClose,
  onAddUsers,
  onLinkGroups,
  keycloakConfigured,
}: GrantAccessModalProps): JSX.Element {
  const [tab, setTab] = useState<'people' | 'groups'>('people');
  const [role, setRole] = useState<SpaceRole>('member');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Люди ──────────────────────────────────────────────────────────────
  const [lensUsers, setLensUsers] = useState<AssignableUser[]>([]);
  const [kcUsers, setKcUsers] = useState<KeycloakDirectoryUser[]>([]);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const debouncedPeople = useDebounced(peopleSearch, SEARCH_DEBOUNCE_MS);

  // ─── Группы ────────────────────────────────────────────────────────────
  const [groups, setGroups] = useState<KeycloakGroup[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  /** Подгруженные подгруппы: id родителя → его дети (пустой массив — детей нет). */
  const [children, setChildren] = useState<Record<string, KeycloakGroup[]>>({});
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [loadingChild, setLoadingChild] = useState<string | null>(null);
  const debouncedGroups = useDebounced(groupSearch, SEARCH_DEBOUNCE_MS);

  // Ответ устаревшего запроса не должен затирать свежий результат.
  const peopleReq = useRef(0);
  const groupsReq = useRef(0);

  useEffect(() => {
    if (!open) {
      setSelectedPeople([]);
      setSelectedGroups([]);
      setPeopleSearch('');
      setGroupSearch('');
      setChildren({});
      setExpandedGroups([]);
      setError(null);
      setGroupsError(null);
    }
  }, [open]);

  // Кандидаты из Lens грузятся один раз на открытие: список небольшой,
  // фильтруем его на клиенте.
  useEffect(() => {
    if (!open) return;
    setPeopleLoading(true);
    listAssignableUsers(spaceId)
      .then((res) => setLensUsers(res.users))
      .catch(() => setLensUsers([]))
      .finally(() => setPeopleLoading(false));
  }, [open, spaceId]);

  // В Keycloak идём только по непустому запросу — иначе вытянули бы весь
  // каталог банка.
  useEffect(() => {
    if (!open || tab !== 'people' || !keycloakConfigured) return;
    const query = debouncedPeople.trim();
    if (query.length < 2) {
      setKcUsers([]);
      return;
    }
    const req = ++peopleReq.current;
    setPeopleLoading(true);
    searchKeycloakUsers(spaceId, query)
      .then((res) => {
        if (peopleReq.current === req) setKcUsers(res.users);
      })
      .catch(() => {
        if (peopleReq.current === req) setKcUsers([]);
      })
      .finally(() => {
        if (peopleReq.current === req) setPeopleLoading(false);
      });
  }, [open, tab, spaceId, debouncedPeople, keycloakConfigured]);

  useEffect(() => {
    if (!open || tab !== 'groups' || !keycloakConfigured) return;
    const req = ++groupsReq.current;
    setGroupsLoading(true);
    setGroupsError(null);
    searchKeycloakGroups(spaceId, debouncedGroups.trim())
      .then((res) => {
        if (groupsReq.current !== req) return;
        setGroups(res.groups);
        setChildren({});
        setExpandedGroups([]);
      })
      .catch((err) => {
        if (groupsReq.current !== req) return;
        setGroups([]);
        setGroupsError(
          err instanceof ApiError ? err.message : 'Не удалось получить список групп',
        );
      })
      .finally(() => {
        if (groupsReq.current === req) setGroupsLoading(false);
      });
  }, [open, tab, spaceId, debouncedGroups, keycloakConfigured]);

  const memberSourceById = useMemo(
    () => new Map(members.map((m) => [m.userId, m.source])),
    [members],
  );

  /**
   * Один человек может прийти и из Lens, и из Keycloak — склеиваем по
   * логину, чтобы в таблице не было двух одинаковых строк.
   */
  const people = useMemo<PersonRow[]>(() => {
    const query = peopleSearch.trim().toLowerCase();
    const byLogin = new Map<string, PersonRow>();

    for (const u of lensUsers) {
      if (
        query &&
        !u.login.toLowerCase().includes(query) &&
        !u.displayName.toLowerCase().includes(query) &&
        !(u.email ?? '').toLowerCase().includes(query)
      ) {
        continue;
      }
      byLogin.set(u.login.toLowerCase(), {
        key: u.login.toLowerCase(),
        userId: u.id,
        keycloakId: null,
        login: u.login,
        displayName: u.displayName,
        email: u.email,
        memberSource: memberSourceById.get(u.id) ?? null,
        isNew: false,
        enabled: true,
      });
    }

    for (const u of kcUsers) {
      const key = u.login.toLowerCase();
      const known = byLogin.get(key);
      if (known) {
        // Того, кто уже есть в Lens, добавляем по его id — заводить второй
        // раз не нужно.
        known.keycloakId = u.keycloakId;
        known.email = known.email ?? u.email;
        continue;
      }
      byLogin.set(key, {
        key,
        userId: u.lensUserId,
        keycloakId: u.keycloakId,
        login: u.login,
        displayName: u.displayName,
        email: u.email,
        memberSource: u.lensUserId ? (memberSourceById.get(u.lensUserId) ?? null) : null,
        isNew: u.lensUserId === null,
        enabled: u.enabled,
      });
    }

    return [...byLogin.values()].sort((a, b) => a.login.localeCompare(b.login));
  }, [lensUsers, kcUsers, peopleSearch, memberSourceById]);

  // Имя и логин в одной строке: так на экран влезает вдвое больше людей.
  const peopleColumns: ColumnsType<PersonRow> = [
    {
      title: 'Пользователь',
      key: 'user',
      ellipsis: true,
      render: (_, row) => (
        <>
          <Typography.Text strong>{row.displayName}</Typography.Text>{' '}
          <Typography.Text type="secondary">{row.login}</Typography.Text>
        </>
      ),
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      width: 240,
      ellipsis: true,
      render: (email: string | null) => email ?? '—',
    },
    {
      title: '',
      key: 'status',
      width: 150,
      render: (_, row) => {
        if (row.memberSource === 'manual') return <Tag>уже участник</Tag>;
        if (row.memberSource === 'group') {
          return (
            <Tooltip title="Доступ пришёл из группы Keycloak. Выберите человека и укажите роль — она станет личной и перестанет зависеть от группы.">
              <Tag color="blue">через группу</Tag>
            </Tooltip>
          );
        }
        if (!row.enabled) return <Tag color="red">выключен</Tag>;
        if (row.isNew) return <Tag color="blue">нет в Lens</Tag>;
        return null;
      },
    },
  ];

  /**
   * Раскрывает/сворачивает узел дерева. Дети подгружаются один раз и
   * остаются в памяти до нового поиска.
   */
  const toggleGroup = useCallback(
    async (group: KeycloakGroup) => {
      if (expandedGroups.includes(group.id)) {
        setExpandedGroups((prev) => prev.filter((id) => id !== group.id));
        return;
      }
      setExpandedGroups((prev) => [...prev, group.id]);
      if (children[group.id]) return;

      setLoadingChild(group.id);
      try {
        const res = await listKeycloakGroupChildren(spaceId, group.id);
        setChildren((prev) => ({ ...prev, [group.id]: res.groups }));
      } catch (err) {
        setChildren((prev) => ({ ...prev, [group.id]: [] }));
        setGroupsError(
          err instanceof ApiError ? err.message : 'Не удалось получить подгруппы',
        );
      } finally {
        setLoadingChild(null);
      }
    },
    [expandedGroups, children, spaceId],
  );

  /**
   * Дерево разворачиваем в плоский список со ступеньками: так строки
   * подгрупп остаются обычными строками таблицы и их можно отмечать
   * галочкой наравне с корневыми.
   */
  const groupRows = useMemo<GroupRow[]>(() => {
    const out: GroupRow[] = [];
    const walk = (list: KeycloakGroup[], level: number): void => {
      for (const g of list) {
        out.push({ ...g, level });
        if (expandedGroups.includes(g.id)) walk(children[g.id] ?? [], level + 1);
      }
    };
    walk(groups, 0);
    return out;
  }, [groups, children, expandedGroups]);

  const groupColumns: ColumnsType<GroupRow> = [
    {
      title: 'Группа',
      key: 'group',
      render: (_, row) => {
        // Стрелку прячем, только когда точно знаем, что детей нет:
        // Keycloak старых версий про это молчит (hasChildren = null).
        const known = children[row.id];
        const canExpand = known ? known.length > 0 : row.hasChildren !== false;
        return (
          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: row.level * 20 }}>
            {canExpand ? (
              <Button
                type="text"
                size="small"
                loading={loadingChild === row.id}
                icon={
                  expandedGroups.includes(row.id) ? <DownOutlined /> : <RightOutlined />
                }
                onClick={() => void toggleGroup(row)}
                data-testid={`grant-access-group-expand-${row.id}`}
              />
            ) : (
              <span style={{ display: 'inline-block', width: 24 }} />
            )}
            <Space direction="vertical" size={0} style={{ marginLeft: 4 }}>
              <Typography.Text strong>{row.name}</Typography.Text>
              <Typography.Text type="secondary">{row.path}</Typography.Text>
            </Space>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'status',
      width: 200,
      render: (_, row) =>
        row.linked ? (
          <Tag>уже привязана</Tag>
        ) : row.level > 0 ? (
          <Tag color="default">подгруппа</Tag>
        ) : null,
    },
  ];

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (tab === 'people') {
        const chosen = people.filter((p) => selectedPeople.includes(p.key));
        await onAddUsers({
          userIds: chosen.map((p) => p.userId).filter((id): id is string => Boolean(id)),
          keycloakIds: chosen
            .filter((p) => !p.userId && p.keycloakId)
            .map((p) => p.keycloakId!),
          role,
        });
      } else {
        await onLinkGroups({ keycloakIds: selectedGroups, role });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось выдать доступ');
    } finally {
      setSubmitting(false);
    }
  }, [tab, people, selectedPeople, selectedGroups, role, onAddUsers, onLinkGroups, onClose]);

  const selectedCount = tab === 'people' ? selectedPeople.length : selectedGroups.length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={960}
      style={{ top: 32 }}
      title="Выдать доступ"
      destroyOnClose
      data-testid="grant-access-modal"
      footer={
        <Space>
          <Typography.Text type="secondary">Роль</Typography.Text>
          <Select<SpaceRole>
            value={role}
            onChange={setRole}
            style={{ width: 180 }}
            options={SPACE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
            data-testid="grant-access-role"
          />
          <Button onClick={onClose}>Отмена</Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={selectedCount === 0}
            onClick={() => void submit()}
            data-testid="grant-access-submit"
          >
            {tab === 'people'
              ? `Добавить${selectedCount > 0 ? ` (${selectedCount})` : ''}`
              : `Привязать${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
          </Button>
        </Space>
      }
    >
      {error ? (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="grant-access-error"
        />
      ) : null}

      <Tabs
        activeKey={tab}
        onChange={(key) => setTab(key as 'people' | 'groups')}
        items={[
          {
            key: 'people',
            label: (
              <span>
                <UserOutlined style={{ marginRight: 6 }} />
                Люди
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Input.Search
                  allowClear
                  placeholder={
                    keycloakConfigured
                      ? 'Логин, имя или почта — ищем и в Lens, и в Keycloak'
                      : 'Логин, имя или почта'
                  }
                  value={peopleSearch}
                  onChange={(e) => setPeopleSearch(e.target.value)}
                  data-testid="grant-access-people-search"
                />
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Typography.Text type="secondary" data-testid="grant-access-people-count">
                    {`Найдено: ${people.length}`}
                    {selectedPeople.length > 0 ? ` · выбрано: ${selectedPeople.length}` : ''}
                  </Typography.Text>
                  {selectedPeople.length > 0 ? (
                    <Button type="link" size="small" onClick={() => setSelectedPeople([])}>
                      Снять выбор
                    </Button>
                  ) : null}
                </Space>
                <Table<PersonRow>
                  size="small"
                  rowKey="key"
                  loading={peopleLoading}
                  columns={peopleColumns}
                  dataSource={people}
                  pagination={false}
                  scroll={{ y: LIST_HEIGHT }}
                  data-testid="grant-access-people-table"
                  rowSelection={{
                    selectedRowKeys: selectedPeople,
                    onChange: (keys) => setSelectedPeople(keys as string[]),
                    getCheckboxProps: (row) => ({
                      disabled: row.memberSource === 'manual' || !row.enabled,
                    }),
                  }}
                />
              </Space>
            ),
          },
          {
            key: 'groups',
            label: (
              <span>
                <TeamOutlined style={{ marginRight: 6 }} />
                Группы Keycloak
              </span>
            ),
            children: keycloakConfigured ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message="Доступ по группе обновляется сам"
                  description={
                    'Привязка покрывает и вложенные группы. Кого добавят позже — ' +
                    'получит доступ при входе в Lens, кого уберут — потеряет. ' +
                    'Роль у всех одна.'
                  }
                />
                {groupsError ? (
                  <Alert type="error" message={groupsError} showIcon />
                ) : null}
                <Input.Search
                  allowClear
                  placeholder="Название группы — или раскройте дерево ниже"
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  data-testid="grant-access-group-search"
                />
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Typography.Text type="secondary" data-testid="grant-access-groups-count">
                    {`Групп: ${groupRows.length}`}
                    {selectedGroups.length > 0 ? ` · выбрано: ${selectedGroups.length}` : ''}
                  </Typography.Text>
                  {selectedGroups.length > 0 ? (
                    <Button type="link" size="small" onClick={() => setSelectedGroups([])}>
                      Снять выбор
                    </Button>
                  ) : null}
                </Space>
                <Table<GroupRow>
                  size="small"
                  rowKey="id"
                  loading={groupsLoading}
                  columns={groupColumns}
                  dataSource={groupRows}
                  pagination={false}
                  scroll={{ y: LIST_HEIGHT }}
                  data-testid="grant-access-groups-table"
                  rowSelection={{
                    selectedRowKeys: selectedGroups,
                    onChange: (keys) => setSelectedGroups(keys as string[]),
                    getCheckboxProps: (row) => ({ disabled: row.linked }),
                  }}
                />
              </Space>
            ) : (
              <Alert
                type="warning"
                showIcon
                message="Вход через Keycloak не настроен"
                description="Заполните реквизиты клиента в настройках платформы — тогда можно будет выдавать доступ целой группе."
              />
            ),
          },
        ]}
      />
    </Modal>
  );
}
